import React from "react";
import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";

import {
  Button,
  Checkbox,
  Select,
  TextArea,
  TextField,
} from "../../../ui/index.ts";
import {
  contactStepSchema,
  type ContactStepValues,
} from "../../create-employee/schemas/create-employee.schema.ts";
import { emergencyContactRelationOptions } from "../../../../../options/emergencyContactRelationOptions.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { useHrSetupUpdateEmployee } from "../hooks/use-hr-setup-update-employee.ts";
import type { HrSetupEmployeeRow } from "../schemas/hr-setup-employee.schema.ts";
import {
  mapOnboardUserToContactStep,
  mapOnboardUserToPersonalStep,
} from "../utils/map-onboard-user-to-form.ts";
import { ApiError } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";

import styles from "./hr-contact-detail-tab.module.css";

export type HrContactDetailTabProps = {
  employee: HrSetupEmployeeRow;
  onSuccess: () => void;
};

const field = (name: keyof ContactStepValues) => name;

function digitsPhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

export function HrContactDetailTab({ employee, onSuccess }: HrContactDetailTabProps) {
  const [editing, setEditing] = useState(false);
  const [sameAsPresent, setSameAsPresent] = useState(false);
  const [invalidEmergencyCharacters, setInvalidEmergencyCharacters] =
    useState(false);
  const [invalidEmergencyName, setInvalidEmergencyName] = useState(false);
  const { updateEmployee, loading } = useHrSetupUpdateEmployee();

  const personal = useMemo(() => mapOnboardUserToPersonalStep(employee), [employee]);

  const formDefaultValues = useMemo(
    (): ContactStepValues => mapOnboardUserToContactStep(employee),
    [employee],
  );

  const form = useForm({
    defaultValues: formDefaultValues,
    validators: { onSubmit: contactStepSchema as never },
    onSubmit: async ({ value }) => {
      try {
        await updateEmployee({ ...personal, ...value, id: employee.id });
        showSuccess("Contact details updated.");
        setEditing(false);
        onSuccess();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Failed to update employee.";
        showError(msg);
      }
    },
  });

  const readOnly = !editing;
  const fieldDisabled = readOnly || loading;

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.toolbar}>
        {!editing ? (
          <Button type="button" variant="muted-outline" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : null}
      </div>

      <div className={styles.grid}>
        <form.Field name={field("phoneNumber")}>
          {(f) => (
            <TextField
              id="hr-setup-contact-phone"
              label="Contact Number"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={f.state.value}
              onChange={(e) => f.handleChange(digitsPhone(e.target.value))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("alternateNumber")}>
          {(f) => (
            <TextField
              id="hr-setup-contact-alternate-number"
              label="Emergency Contact Number"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={f.state.value}
              onChange={(e) => {
                setInvalidEmergencyCharacters(/\D/.test(e.target.value));
                f.handleChange(digitsPhone(e.target.value));
              }}
              onBlur={f.handleBlur}
              error={
                invalidEmergencyCharacters
                  ? "Emergency contact number must contain digits only."
                  : formatFormFieldErrors(f.state.meta.errors)
              }
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("relationWithAlternateNo")}>
          {(f) => (
            <Select
              id="hr-setup-contact-relation"
              label="Relationship with Emergency Contact Person"
              required
              value={f.state.value}
              onChange={(v) => f.handleChange(v as ContactStepValues["relationWithAlternateNo"])}
              options={emergencyContactRelationOptions}
              placeholder="Select Relation"
              disabled={fieldDisabled}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Field name={field("alternateName")}>
          {(f) => (
            <TextField
              id="hr-setup-contact-alternate-name"
              label="Emergency Contact Name"
              maxLength={100}
              value={f.state.value}
              onChange={(e) => {
                setInvalidEmergencyName(/\d/.test(e.target.value));
                f.handleChange(e.target.value.replace(/\d/g, ""));
              }}
              onBlur={f.handleBlur}
              error={
                invalidEmergencyName
                  ? "Emergency contact name must not contain numbers."
                  : formatFormFieldErrors(f.state.meta.errors)
              }
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
      </div>

      <div className={styles.textareas}>
        <form.Field name={field("presentAddress")}>
          {(f) => (
            <TextArea
              id="hr-setup-contact-present-address"
              label="Current Residential Address"
              rows={4}
              value={f.state.value}
              onChange={(e) => {
                const v = e.target.value;
                f.handleChange(v);
                if (sameAsPresent) {
                  form.setFieldValue("permanentAddress", v);
                }
              }}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={fieldDisabled}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("permanentAddress")}>
          {(f) => (
            <div>
              <TextArea
                id="hr-setup-contact-permanent-address"
                label="Permanent Address"
                rows={4}
                className={sameAsPresent ? styles.permanentLocked : undefined}
                value={f.state.value}
                onChange={(e) => {
                  if (sameAsPresent) return;
                  f.handleChange(e.target.value);
                }}
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={fieldDisabled}
                readOnly={sameAsPresent}
                required
              />
              {editing ? (
                <div className={styles.sameRow}>
                  <form.Subscribe selector={(s) => s.values.presentAddress}>
                    {(present) => (
                      <Checkbox
                        id="hr-setup-contact-same-address"
                        label="Same as Residential Address"
                        checked={sameAsPresent}
                        disabled={fieldDisabled}
                        onChange={(checked) => {
                          setSameAsPresent(checked);
                          if (checked) {
                            form.setFieldValue("permanentAddress", present);
                          }
                        }}
                      />
                    )}
                  </form.Subscribe>
                </div>
              ) : null}
            </div>
          )}
        </form.Field>
      </div>

      <div className={styles.actions}>
        {editing ? (<><Button
          type="button"
          variant="muted-outline"
          disabled={loading}
          onClick={() => {
            form.reset(formDefaultValues);
            setSameAsPresent(false);
            setEditing(false);
          }}
        >
          Cancel
        </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Updating…" : "Update"}
          </Button></>) : <div className={styles.filler}></div>}
      </div>
    </form>
  );
}
